import React from 'react';
import { Descriptions as AntDescriptions, DescriptionsProps as AntDescriptionsProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface DescriptionsProps extends AntDescriptionsProps {
  className?: string;
}

export type DescriptionsItemProps = React.ComponentProps<typeof AntDescriptions.Item>;

const useStyles = createUseStyles((theme: ThemeType) => ({
  descriptions: {
    '& .ant-descriptions-header': {
      marginBottom: theme.spacing[4],
    },
    '& .ant-descriptions-title': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      lineHeight: theme.typography.lineHeight.normal,
    },
    '& .ant-descriptions-extra': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
    },
    '& .ant-descriptions-item-label': {
      color: theme.color.semantic.text.tertiary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      lineHeight: theme.typography.lineHeight.normal,
    },
    '& .ant-descriptions-item-content': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
      lineHeight: theme.typography.lineHeight.normal,
    },
    '& .ant-descriptions-row > th, & .ant-descriptions-row > td': {
      paddingBottom: theme.spacing[4],
    },
    '&.ant-descriptions-middle .ant-descriptions-row > th, &.ant-descriptions-middle .ant-descriptions-row > td': {
      paddingBottom: theme.spacing[3],
    },
    '&.ant-descriptions-small .ant-descriptions-row > th, &.ant-descriptions-small .ant-descriptions-row > td': {
      paddingBottom: theme.spacing[2],
    },
    '&.ant-descriptions-bordered .ant-descriptions-view': {
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      borderRadius: theme.radius.sm,
    },
    '&.ant-descriptions-bordered .ant-descriptions-row': {
      borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
    },
    '&.ant-descriptions-bordered .ant-descriptions-row:last-child': {
      borderBottom: 'none',
    },
    '&.ant-descriptions-bordered .ant-descriptions-item-label, &.ant-descriptions-bordered .ant-descriptions-item-content': {
      padding: `${theme.spacing[4]} ${theme.spacing[6]}`,
      borderRight: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
    },
    '&.ant-descriptions-bordered .ant-descriptions-item-label:last-child, &.ant-descriptions-bordered .ant-descriptions-item-content:last-child': {
      borderRight: 'none',
    },
    '&.ant-descriptions-bordered .ant-descriptions-item-label': {
      backgroundColor: theme.color.semantic.brand.default.subtle,
    },
    '&.ant-descriptions-bordered .ant-descriptions-item-content': {
      backgroundColor: theme.color.semantic.background.neutral.secondary,
    },
    '&.ant-descriptions-bordered.ant-descriptions-middle .ant-descriptions-item-label, &.ant-descriptions-bordered.ant-descriptions-middle .ant-descriptions-item-content': {
      padding: `${theme.spacing[3]} ${theme.spacing[6]}`,
    },
    '&.ant-descriptions-bordered.ant-descriptions-small .ant-descriptions-item-label, &.ant-descriptions-bordered.ant-descriptions-small .ant-descriptions-item-content': {
      padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    },
  },
}));

const DescriptionsComponent: React.FC<DescriptionsProps> = ({ className = '', ...rest }) => {
  const classes = useStyles();
  return <AntDescriptions {...rest} className={`${classes.descriptions} ${className}`.trim()} />;
};

const Descriptions = Object.assign(DescriptionsComponent, {
  Item: AntDescriptions.Item,
});

export default Descriptions;
